/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  AuthConfigService,
  DeviceBindingService,
  DomainTokensService,
  DeviceProvisionServiceService,
  TivoIdentityAuthServiceService
} from "@tivocorp/data-domain-lib";
import type {
  AuthConfiguration,
  AuthConfigurations,
  AuthConfigurationV3,
  AuthConfigurationsV3,
  AuthenticationType,
  AuthenticationTypeV3,
  Bind,
  DbsJwtTokenRequest,
  DbsJwtTokenResponse,
  DomainToken,
  IpAutologinTokenResponse,
  LicensePlateRequestV2,
  LicensePlateResponseV2,
  ProvisionDevicesResponse,
  ProvisionDevicesResponseV2,
  Success,
  TAuthConfigGetAuthConfigurationsParams,
  TAuthConfigGetAuthConfigurationsV3Params,
  TDomainTokensGetClaimParams,
  TDomainTokensGetDomainTokenParams,
  TDomainTokensGetDeviceDomainTokenParams,
  TDomainTokensGetScopedDomainTokenParams,
  TDomainTokensGetBindParams,
  TDeviceProvisionServiceProvisionDevicesParams,
  TDeviceProvisionServiceProvisionDevicesV2Params,
  TServicesParams
} from "@tivocorp/data-domain-lib";

import type {
  ISignInInteractor,
  UpdatePasswordSuccessResponse,
  ValidateEmailTokenResponse,
  ValidateEmailTokenResult
} from "../../interfaces/ISignInInteractor";
import { Log } from "../../logging/Log";
import { SignInApi, SignInLogger } from "../../logging/SignInLogger";
import { DiagnosticSeverity } from "../../types";
import { Locale, LocaleKeys } from "../../locale";
import { CommonHeaders } from "../../CommonHeaders";
import { TsriEndpointResolver } from "../../tsri";
import { ApplicationFeatureArea } from "../../enums";
import {
  AuthConfigServiceError,
  BindServiceError,
  ClaimServiceError,
  DeleteDeviceBindingServiceError,
  DeviceProvisionServiceError,
  DomainTokenServiceError,
  EmptyResponseError,
  ErrorTypes,
  ForgotPasswordServiceError,
  IDP_JWT_TOKEN_EMPTY_TOKEN_CODE,
  IdpJwtTokenServiceError,
  IpAutologinServiceError,
  PartialResponseError,
  UpdatePasswordServiceError,
  ValidateEmailTokenServiceError
} from "../../error";
import { attachHttpContext } from "../../error/attachHttpContext";
import { DeviceModel } from "../../device";
import { AppStartupInteractor } from "./appStartupInteractor";
import { TIVO_SERVICES } from "../../../config";
import { STORAGE_KEY_OVERRIDE_IP_AUTOLOGIN } from "../../storage";
import { transformBaseUrl } from "../../../devtools/mockOverride";

/**
 * Facade class that combines AuthConfigService, DomainTokensService,
 * and the raw IDP JWT fetch behind a single entry point for the
 * sign-in flow.
 *
 * Service instances are **not** stored as class members. A fresh
 * instance is created on every method call so the interactor holds
 * no mutable state. TServicesParams is built internally; callers
 * do not supply it.
 */
export class SignInInteractor implements ISignInInteractor {
  private static TAG: string = "SignInInteractor";

  private static getTemporaryServiceIssueDescription(): string {
    return Locale.getString(
      LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
    );
  }

  private static createEmptyResponseError(
    code: string,
    message: string
  ): EmptyResponseError {
    return new EmptyResponseError(
      code,
      message,
      SignInInteractor.getTemporaryServiceIssueDescription()
    );
  }

  private static createPartialResponseError(
    code: string,
    message: string
  ): PartialResponseError {
    return new PartialResponseError(
      code,
      message,
      SignInInteractor.getTemporaryServiceIssueDescription()
    );
  }

  private static extractErrorContext(error: unknown): {
    responseData: unknown;
    serverCode: string | undefined;
    clientCode: string;
    transportMessage: string | undefined;
  } {
    const responseData: unknown =
      error !== null &&
      typeof error === "object" &&
      "response" in error &&
      error.response !== null &&
      typeof error.response === "object" &&
      "data" in error.response
        ? error.response.data
        : undefined;
    const serverCode: string | undefined =
      responseData !== null &&
      typeof responseData === "object" &&
      "code" in responseData
        ? String(responseData.code)
        : undefined;
    const clientCode: string =
      error !== null && typeof error === "object" && "code" in error
        ? String(error.code)
        : "";
    const transportMessage: string | undefined =
      error !== null &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
        ? error.message
        : undefined;

    return {
      responseData,
      serverCode,
      clientCode,
      transportMessage
    };
  }

  /**
   * Authentication type that requires a license plate URL to be present in
   * the auth configuration response.
   */
  private static DBS_LICENSE_PLATE_BINDING: string = "dbsLicensePlateBinding";

  /**
   * Builds an AuthConfigServiceError from a caught error. A service error code
   * (error.response.data.code) is preferred; otherwise the HTTP client (axios)
   * error code is used so that connection/timeout failures are mapped.
   */
  private static buildAuthConfigServiceError(
    error: unknown
  ): AuthConfigServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : "Auth configuration fetch failed";
    const serviceError = new AuthConfigServiceError(
      serverCode !== undefined ? serverCode : clientCode,
      message
    );
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a ClaimServiceError from a caught error. A service error code
   * (error.response.data.code) is preferred; otherwise the HTTP client (axios)
   * error code is used so that connection/timeout failures are mapped.
   */
  private static buildClaimServiceError(error: unknown): ClaimServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    let message: string = Locale.getString(LocaleKeys.CLAIM_API_FETCH_FAILED);
    if (
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
    ) {
      message = String(responseData.message);
    }
    const serviceError = new ClaimServiceError(
      serverCode !== undefined ? serverCode : clientCode,
      message
    );
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a DomainTokenServiceError from a caught error. A service error code
   * (error.response.data.code) is preferred; otherwise the HTTP client (axios)
   * error code is used so that connection/timeout failures are mapped.
   */
  private static buildDomainTokenServiceError(
    error: unknown
  ): DomainTokenServiceError | undefined {
    if (typeof DomainTokenServiceError !== "function") {
      return undefined;
    }
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : "Domain token fetch failed";
    const serviceError = new DomainTokenServiceError(
      serverCode !== undefined ? serverCode : clientCode,
      message
    );
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a DeviceProvisionServiceError from a caught error. A service error
   * code (error.response.data.code) is preferred; when a response exists but
   * has no payload the NO_PAYLOAD sentinel is used; otherwise the HTTP client
   * (axios) error code is used so that connection/timeout failures are mapped.
   */
  private static buildDeviceProvisionServiceError(
    error: unknown
  ): DeviceProvisionServiceError {
    const hasResponse: boolean =
      error !== null &&
      typeof error === "object" &&
      "response" in error &&
      error.response !== null &&
      typeof error.response === "object";
    const responseData: unknown =
      error !== null &&
      typeof error === "object" &&
      "response" in error &&
      error.response !== null &&
      typeof error.response === "object" &&
      "data" in error.response
        ? error.response.data
        : undefined;
    const serverCode: string | undefined =
      responseData !== null &&
      responseData !== undefined &&
      typeof responseData === "object" &&
      "code" in responseData
        ? String(responseData.code)
        : undefined;
    const noPayload: boolean =
      hasResponse &&
      (responseData === undefined ||
        responseData === null ||
        responseData === "");
    const clientCode: string =
      error !== null && typeof error === "object" && "code" in error
        ? String(error.code)
        : "";
    const message: string =
      responseData !== null &&
      responseData !== undefined &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : Locale.getString(LocaleKeys.DEVICE_PROVISIONING_ERROR_MESSAGE);
    let code: string;
    if (serverCode !== undefined) {
      code = serverCode;
    } else if (noPayload) {
      code = "NO_PAYLOAD";
    } else {
      code = clientCode;
    }
    const serviceError = new DeviceProvisionServiceError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a BindServiceError for /bind failures.
   */
  private static buildBindServiceError(error: unknown): BindServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    let message: string = Locale.getString(LocaleKeys.BIND_API_FETCH_FAILED);
    if (
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
    ) {
      message = String(responseData.message);
    }
    const serviceError = new BindServiceError(
      serverCode !== undefined ? serverCode : clientCode,
      message
    );
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds an IpAutologinServiceError from a caught error. The service
   * returns a flat body ({code, message}) with codes namespaced as
   * "IAS.<CODE>" (e.g. "IAS.PCID_NOT_FOUND"); a nested {error:{code,message}}
   * envelope is also supported defensively. When no recognised service code is
   * present, the axios client error code is used.
   */
  private static buildIpAutologinServiceError(
    error: unknown
  ): IpAutologinServiceError | undefined {
    if (typeof IpAutologinServiceError !== "function") {
      return undefined;
    }
    const response: unknown =
      error !== null && typeof error === "object" && "response" in error
        ? error.response
        : undefined;
    const responseData: unknown =
      response !== null && typeof response === "object" && "data" in response
        ? response.data
        : undefined;
    // Body is normally flat ({code, message}); fall back to a nested
    // {error:{code,message}} envelope if the response ever uses that shape.
    const errorBody: unknown =
      responseData !== null &&
      typeof responseData === "object" &&
      "error" in responseData
        ? responseData.error
        : responseData;
    const rawServerCode: string | undefined =
      errorBody !== null && typeof errorBody === "object" && "code" in errorBody
        ? String(errorBody.code)
        : undefined;
    // Service namespaces its codes (e.g. "IAS.PCID_NOT_FOUND"); strip the
    // prefix so it matches the bare codes IpAutologinServiceError expects.
    const IAS_CODE_PREFIX_PATTERN: RegExp = /^IAS\./;
    const serverCode: string | undefined =
      rawServerCode !== undefined
        ? rawServerCode.replace(IAS_CODE_PREFIX_PATTERN, "")
        : undefined;
    const clientCode: string =
      error !== null && typeof error === "object" && "code" in error
        ? String(error.code)
        : "";
    const message: string =
      errorBody !== null &&
      typeof errorBody === "object" &&
      "message" in errorBody
        ? String(errorBody.message)
        : Locale.getString(
            LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
          );
    const code: string = serverCode !== undefined ? serverCode : clientCode;
    const serviceError = new IpAutologinServiceError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a DeleteDeviceBindingServiceError from a caught error. A service
   * error code (error.response.data.code) is preferred; otherwise the HTTP
   * client (axios) error code is used so that connection/timeout failures
   * are mapped.
   */
  private static buildDeviceBindingServiceError(
    error: unknown
  ): DeleteDeviceBindingServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    let message: string = Locale.getString(
      LocaleKeys.DEVICE_BINDING_API_FETCH_FAILED
    );
    if (
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
    ) {
      message = String(responseData.message);
    }
    const serviceError = new DeleteDeviceBindingServiceError(
      serverCode !== undefined ? serverCode : clientCode,
      message
    );
    serviceError.setDescription(message);
    // Deliberately no attachHttpContext: DBS error handling (license-plate
    // retry and polling) is being reworked separately, and exposing the HTTP
    // status here would switch on the 401/403 terminal backstop for DBS calls
    // ahead of that work.
    return serviceError;
  }

  /**
   * identity-auth-service namespaces its service error codes as "IAS.<CODE>"
   * (e.g. "IAS.USER_NOT_FOUND"); strip the prefix so it matches the bare
   * codes ForgotPasswordServiceError/ValidateEmailTokenServiceError/
   * UpdatePasswordServiceError expect.
   */
  private static IAS_CODE_PREFIX_PATTERN: RegExp = /^IAS\./;

  /**
   * Builds a ForgotPasswordServiceError from a caught error. A service error
   * code (error.response.data.code) is preferred; otherwise the HTTP client
   * (axios) error code is used so that connection/timeout failures are mapped.
   */
  private static buildForgotPasswordServiceError(
    error: unknown
  ): ForgotPasswordServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : Locale.getString(LocaleKeys.FORGOT_PASSWORD_API_FETCH_FAILED);
    const code: string =
      serverCode !== undefined
        ? serverCode.replace(SignInInteractor.IAS_CODE_PREFIX_PATTERN, "")
        : clientCode;
    const serviceError = new ForgotPasswordServiceError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds an IdpJwtTokenServiceError from a caught error. A service error code
   * (error.response.data.code) is preferred; otherwise the HTTP client (axios)
   * error code is used so that connection/timeout failures are mapped.
   *
   * Until this existed `fetchIdpJwtToken` rethrew the raw axios error, so the
   * view had no `getCode()` to read, every failure resolved to the `A010`
   * sign-in catch-all, and the error alert fell back to printing the service's
   * own "IAS.INTERNAL_ERROR" on screen. This is step 3 of the sign-in walk and
   * where a mistyped password lands, so it was also the most-seen sign-in
   * error with no client code of its own.
   */
  private static buildIdpJwtTokenServiceError(
    error: unknown
  ): IdpJwtTokenServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : Locale.getString(LocaleKeys.IDP_JWT_TOKEN_API_FETCH_FAILED);
    const code: string =
      serverCode !== undefined
        ? serverCode.replace(SignInInteractor.IAS_CODE_PREFIX_PATTERN, "")
        : clientCode;
    const serviceError = new IdpJwtTokenServiceError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a ValidateEmailTokenServiceError from a caught error. A service
   * error code (error.response.data.code) is preferred; otherwise the HTTP
   * client (axios) error code is used so that connection/timeout failures
   * are mapped.
   */
  private static buildValidateEmailTokenServiceError(
    error: unknown
  ): ValidateEmailTokenServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : Locale.getString(LocaleKeys.VALIDATE_EMAIL_TOKEN_API_FETCH_FAILED);
    const code: string =
      serverCode !== undefined
        ? serverCode.replace(SignInInteractor.IAS_CODE_PREFIX_PATTERN, "")
        : clientCode;
    const serviceError = new ValidateEmailTokenServiceError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds an UpdatePasswordServiceError from a caught error. A service error
   * code (error.response.data.code) is preferred; otherwise the HTTP client
   * (axios) error code is used so that connection/timeout failures are mapped.
   */
  private static buildUpdatePasswordServiceError(
    error: unknown
  ): UpdatePasswordServiceError {
    const { responseData, serverCode, clientCode } =
      SignInInteractor.extractErrorContext(error);
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : Locale.getString(LocaleKeys.UPDATE_PASSWORD_API_FETCH_FAILED);
    const code: string =
      serverCode !== undefined
        ? serverCode.replace(SignInInteractor.IAS_CODE_PREFIX_PATTERN, "")
        : clientCode;
    const serviceError = new UpdatePasswordServiceError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Validates the V2 authConfigurations response. Throws when the response
   * cannot be demarshalled (A114), is partial (A112), is missing a domain
   * (A103), or is missing a license plate URL for a license-plate binding
   * configuration (A104).
   */
  private validateAuthConfigResponse(response: AuthConfigurations): void {
    const configs: AuthConfiguration[] | undefined =
      response.authconfigurations;
    if (configs === undefined || configs === null || !Array.isArray(configs)) {
      throw new EmptyResponseError(
        ErrorTypes.AUTH_CONFIG_DEMARSHAL_ERROR_A114,
        Locale.getString(LocaleKeys.AUTH_CONFIG_DEMARSHALLED_ERROR_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (configs.length === 0) {
      throw new PartialResponseError(
        ErrorTypes.AUTH_CONFIG_PARTIAL_RESPONSE_A112,
        Locale.getString(LocaleKeys.AUTH_CONFIG_PARTIAL_RESPONSE_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    for (let i = 0; i < configs.length; i += 1) {
      const config: AuthConfiguration = configs[i];
      if (
        config.domain === undefined ||
        config.domain === null ||
        config.domain === ""
      ) {
        throw new EmptyResponseError(
          ErrorTypes.AUTH_CONFIG_NO_DOMAIN_A103,
          Locale.getString(LocaleKeys.AUTH_CONFIG_NO_DOMAIN_MESSAGE),
          Locale.getString(
            LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
          )
        );
      }
      if (
        config.authenticationType ===
          SignInInteractor.DBS_LICENSE_PLATE_BINDING &&
        (config.licensePlateUrl === undefined ||
          config.licensePlateUrl === null ||
          config.licensePlateUrl === "")
      ) {
        throw new EmptyResponseError(
          ErrorTypes.AUTH_CONFIG_NO_LICENSE_PLATE_URL_A104,
          Locale.getString(LocaleKeys.AUTH_CONFIG_NO_LICENSE_PLATE_URL_MESSAGE),
          Locale.getString(
            LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
          )
        );
      }
    }
  }

  /**
   * Validates the V3 authConfigurations response. Throws when the response
   * cannot be demarshalled (A114), is partial (A112), is missing an
   * authorization scheme (A116), or is missing a license plate binding URL for
   * a license-plate binding configuration (A104).
   */
  private validateAuthConfigResponseV3(response: AuthConfigurationsV3): void {
    const configs: AuthConfigurationV3[] | undefined =
      response.authconfigurations;
    if (configs === undefined || configs === null || !Array.isArray(configs)) {
      throw new EmptyResponseError(
        ErrorTypes.AUTH_CONFIG_DEMARSHAL_ERROR_A114,
        Locale.getString(LocaleKeys.AUTH_CONFIG_DEMARSHALLED_ERROR_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (configs.length === 0) {
      throw new PartialResponseError(
        ErrorTypes.AUTH_CONFIG_PARTIAL_RESPONSE_A112,
        Locale.getString(LocaleKeys.AUTH_CONFIG_PARTIAL_RESPONSE_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    for (let i = 0; i < configs.length; i += 1) {
      const config: AuthConfigurationV3 = configs[i];
      if (
        config.authorizationSchemes === undefined ||
        config.authorizationSchemes === null
      ) {
        throw new EmptyResponseError(
          ErrorTypes.AUTH_CONFIG_NO_AUTHORIZATION_SCHEME_A116,
          Locale.getString(
            LocaleKeys.AUTH_CONFIG_NO_AUTHORIZATION_SCHEME_MESSAGE
          ),
          Locale.getString(
            LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
          )
        );
      }
      if (
        config.authenticationType === SignInInteractor.DBS_LICENSE_PLATE_BINDING
      ) {
        const licensePlateBindingUrl: string | undefined =
          config.dbsMetaData !== undefined && config.dbsMetaData !== null
            ? config.dbsMetaData.licensePlateBindingUrl
            : undefined;
        if (
          licensePlateBindingUrl === undefined ||
          licensePlateBindingUrl === null ||
          licensePlateBindingUrl === ""
        ) {
          throw new EmptyResponseError(
            ErrorTypes.AUTH_CONFIG_NO_LICENSE_PLATE_URL_A104,
            Locale.getString(
              LocaleKeys.AUTH_CONFIG_NO_LICENSE_PLATE_URL_MESSAGE
            ),
            Locale.getString(
              LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
            )
          );
        }
      }
    }
  }

  /**
   * Validates the claim response. Throws when the response cannot be
   * demarshalled (A124) or is empty (A122).
   */
  private validateClaimResponse(response: Success): void {
    if (
      response === undefined ||
      response === null ||
      typeof response !== "object"
    ) {
      throw SignInInteractor.createEmptyResponseError(
        ErrorTypes.CLAIM_DEMARSHAL_ERROR_A904,
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (
      !("message" in response) ||
      typeof response.message !== "string" ||
      response.message === ""
    ) {
      throw SignInInteractor.createPartialResponseError(
        ErrorTypes.CLAIM_PARTIAL_RESPONSE_A902,
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
  }

  /**
   * Validates the domain token response. Throws when the response cannot be
   * demarshalled (A441) or is empty (A440).
   */
  private validateDomainTokenResponse(response: DomainToken): void {
    if (
      response === undefined ||
      response === null ||
      typeof response !== "object"
    ) {
      throw SignInInteractor.createEmptyResponseError(
        ErrorTypes.DOMAIN_TOKEN_DEMARSHAL_ERROR_A441,
        "fetchDomainToken operation has returned a response that could not be demarshalled"
      );
    }
    const responseKeys: string[] = Object.keys(response);
    if (responseKeys.length === 0) {
      throw SignInInteractor.createPartialResponseError(
        ErrorTypes.DOMAIN_TOKEN_SHORT_RESPONSE_A440,
        "fetchDomainToken operation has returned a partial response"
      );
    }
    // A433 — the call succeeded but carries no token. Previously this fell
    // through as a success and surfaced later as an opaque auth failure.
    if (
      response.token === undefined ||
      response.token === null ||
      response.token === ""
    ) {
      throw SignInInteractor.createEmptyResponseError(
        ErrorTypes.DOMAIN_TOKEN_EMPTY_TOKEN_A433,
        "fetchDomainToken operation is successful but domainToken field is empty"
      );
    }
  }

  /**
   * Validates the ipAutologinToken response. Throws when the response cannot
   * be demarshalled (A508) or the required jwt is missing/empty (A509). The
   * A509 message matches IP_AUTOLOGIN_EMPTY_CREDENTIAL_MESSAGE in
   * signIn.types.ts so the ViewModel can still classify it by message.
   */
  private validateIpAutologinTokenResponse(
    response: IpAutologinTokenResponse
  ): void {
    if (
      response === undefined ||
      response === null ||
      typeof response !== "object"
    ) {
      throw SignInInteractor.createEmptyResponseError(
        ErrorTypes.AUTH_CONFIG_APIGW_ERROR_A106,
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (
      response.jwt === undefined ||
      response.jwt === null ||
      response.jwt.length === 0
    ) {
      throw SignInInteractor.createPartialResponseError(
        ErrorTypes.AUTH_CONFIG_APIGW_ERROR_A106,
        "IP autologin JWT token is empty in service response"
      );
    }
  }

  /**
   * Validates the forgotPassword response. Throws when the response cannot be
   * demarshalled or is missing its status/message fields.
   */
  private validateForgotPasswordResponse(
    response: UpdatePasswordSuccessResponse
  ): void {
    if (
      response === undefined ||
      response === null ||
      typeof response !== "object"
    ) {
      throw new EmptyResponseError(
        ErrorTypes.FORGOT_PASSWORD_DEMARSHAL_ERROR_A929,
        Locale.getString(LocaleKeys.FORGOT_PASSWORD_DEMARSHALLED_ERROR_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (
      response.status === undefined ||
      response.status === null ||
      response.status === "" ||
      response.message === undefined ||
      response.message === null ||
      response.message === ""
    ) {
      throw new PartialResponseError(
        ErrorTypes.FORGOT_PASSWORD_PARTIAL_RESPONSE_A930,
        Locale.getString(LocaleKeys.FORGOT_PASSWORD_PARTIAL_RESPONSE_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
  }

  /**
   * Validates the validateEmailToken response. Throws when the response
   * cannot be demarshalled or a required field is missing or empty: email,
   * identityId, partnerCustomerId, msoName.
   */
  private validateValidateEmailTokenResponse(
    response: ValidateEmailTokenResponse
  ): void {
    if (
      response === undefined ||
      response === null ||
      typeof response !== "object"
    ) {
      throw new EmptyResponseError(
        ErrorTypes.VALIDATE_EMAIL_TOKEN_DEMARSHAL_ERROR_A950,
        Locale.getString(
          LocaleKeys.VALIDATE_EMAIL_TOKEN_DEMARSHALLED_ERROR_MESSAGE
        ),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }

    if (
      response.email === undefined ||
      response.email === null ||
      response.email.length === 0
    ) {
      throw new PartialResponseError(
        ErrorTypes.VALIDATE_EMAIL_TOKEN_MISSING_EMAIL_A951,
        Locale.getString(LocaleKeys.VALIDATE_EMAIL_TOKEN_MISSING_EMAIL_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }

    if (
      response.identityId === undefined ||
      response.identityId === null ||
      response.identityId.length === 0
    ) {
      throw new PartialResponseError(
        ErrorTypes.VALIDATE_EMAIL_TOKEN_MISSING_IDENTITY_ID_A952,
        Locale.getString(
          LocaleKeys.VALIDATE_EMAIL_TOKEN_MISSING_IDENTITY_ID_MESSAGE
        ),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }

    if (
      response.partnerCustomerId === undefined ||
      response.partnerCustomerId === null ||
      response.partnerCustomerId.length === 0
    ) {
      throw new PartialResponseError(
        ErrorTypes.VALIDATE_EMAIL_TOKEN_MISSING_PARTNER_CUSTOMER_ID_A953,
        Locale.getString(
          LocaleKeys.VALIDATE_EMAIL_TOKEN_MISSING_PARTNER_CUSTOMER_ID_MESSAGE
        ),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }

    if (
      response.msoName === undefined ||
      response.msoName === null ||
      response.msoName.length === 0
    ) {
      throw new PartialResponseError(
        ErrorTypes.VALIDATE_EMAIL_TOKEN_MISSING_MSO_NAME_A954,
        Locale.getString(
          LocaleKeys.VALIDATE_EMAIL_TOKEN_MISSING_MSO_NAME_MESSAGE
        ),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
  }

  /**
   * Validates the updatePassword response. Throws when the response cannot be
   * demarshalled, is missing its status field, or the status is not
   * "success".
   */
  private validateUpdatePasswordResponse(
    response: UpdatePasswordSuccessResponse
  ): void {
    if (
      response === undefined ||
      response === null ||
      typeof response !== "object"
    ) {
      throw new EmptyResponseError(
        ErrorTypes.UPDATE_PASSWORD_DEMARSHAL_ERROR_A969,
        Locale.getString(LocaleKeys.UPDATE_PASSWORD_DEMARSHALLED_ERROR_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (
      response.status === undefined ||
      response.status === null ||
      response.status === ""
    ) {
      throw new PartialResponseError(
        ErrorTypes.UPDATE_PASSWORD_PARTIAL_RESPONSE_A970,
        Locale.getString(LocaleKeys.UPDATE_PASSWORD_PARTIAL_RESPONSE_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (response.status.toLowerCase() !== "success") {
      throw new PartialResponseError(
        ErrorTypes.UPDATE_PASSWORD_STATUS_FAILURE_A971,
        Locale.getString(LocaleKeys.UPDATE_PASSWORD_STATUS_FAILURE_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
  }

  /**
   * Builds the TServicesParams used to instantiate AuthConfigService.
   * Common metadata is derived from DeviceModel via CommonHeaders.
   */
  private static async buildAuthConfigServicesParams(): Promise<TServicesParams> {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: transformBaseUrl(
        await new AppStartupInteractor().getServiceEndpointByServiceName(
          TIVO_SERVICES.AuthConfigService
        )
      )
    });
  }

  /**
   * Builds the TServicesParams used to instantiate DomainTokensService.
   * Common metadata is derived from DeviceModel via CommonHeaders.
   */
  private static async buildDomainTokensServicesParams(): Promise<TServicesParams> {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: transformBaseUrl(
        await new AppStartupInteractor().getServiceEndpointByServiceName(
          TIVO_SERVICES.DomainTokens
        )
      )
    });
  }

  /**
   * Builds the TServicesParams used to instantiate DeviceProvisionServiceService.
   * Common metadata is derived from DeviceModel via CommonHeaders.
   */
  private static async buildDeviceProvisionServicesParams(
    domainToken: string
  ): Promise<TServicesParams> {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: transformBaseUrl(
        await new AppStartupInteractor().getServiceEndpointByServiceName(
          TIVO_SERVICES.DeviceProvisionService
        )
      ),
      accessToken: domainToken
    });
  }

  /**
   * Builds the TServicesParams used to instantiate DeviceBindingService.
   * Common metadata is derived from DeviceModel via CommonHeaders.
   * Includes the TivoVerifiedDeviceLoggingId header required by DBS APIs.
   */
  private static async buildDeviceBindingServicesParams(
    dbsToken: string
  ): Promise<TServicesParams> {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    const params: TServicesParams = common.toServicesParams({
      basePath: transformBaseUrl(
        await new AppStartupInteractor().getServiceEndpointByServiceName(
          TIVO_SERVICES.DeviceBindingService
        )
      ),
      accessToken: dbsToken
    });
    params.tivoVerifiedDeviceLoggingId = DeviceModel.getDeviceLoggingId();
    return params;
  }

  public async fetchAuthConfigurations(
    params: TAuthConfigGetAuthConfigurationsParams,
    signal?: AbortSignal
  ): Promise<AuthConfigurations> {
    try {
      Log.d(SignInInteractor.TAG, "fetchAuthConfigurations() called");
      const service: AuthConfigService = new AuthConfigService(
        await SignInInteractor.buildAuthConfigServicesParams()
      );
      const response: AuthConfigurations =
        await service.fetchAuthConfigurations(params, signal);
      this.validateAuthConfigResponse(response);
      return response;
    } catch (error: unknown) {
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "fetchAuthConfigurations() validation failed",
          error
        );
        throw error;
      }
      const authConfigError: AuthConfigServiceError =
        SignInInteractor.buildAuthConfigServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "fetchAuthConfigurations() failed",
        authConfigError
      );
      throw authConfigError;
    }
  }

  public async fetchAuthConfigurationsV3(
    params: TAuthConfigGetAuthConfigurationsV3Params,
    signal?: AbortSignal
  ): Promise<AuthConfigurationsV3> {
    try {
      Log.d(SignInInteractor.TAG, "fetchAuthConfigurationsV3() called");
      const service: AuthConfigService = new AuthConfigService(
        await SignInInteractor.buildAuthConfigServicesParams()
      );
      const response: AuthConfigurationsV3 =
        await service.fetchAuthConfigurationsV3(params, signal);
      this.validateAuthConfigResponseV3(response);
      return response;
    } catch (error: unknown) {
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "fetchAuthConfigurationsV3() validation failed",
          error
        );
        throw error;
      }
      const authConfigError: AuthConfigServiceError =
        SignInInteractor.buildAuthConfigServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "fetchAuthConfigurationsV3() failed",
        authConfigError
      );
      throw authConfigError;
    }
  }

  public getAuthConfigurationByType(
    authenticationType: AuthenticationTypeV3 | AuthenticationType
  ): AuthConfigurationV3 | AuthConfiguration | undefined {
    try {
      Log.d(
        SignInInteractor.TAG,
        "getAuthConfigurationByType() called for type: " + authenticationType
      );
      // Reads the auth config the domain lib already cached — no request is
      // issued, so `basePath` is inert for this call. It is therefore `peek`ed
      // rather than resolved: this method must stay synchronous, and there is
      // no reason to start an endpoint recovery for a URL nothing will use.
      // A genuinely missing auth-config-service surfaces on the fetch paths
      // above, which do resolve it.
      const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
      const service: AuthConfigService = new AuthConfigService(
        common.toServicesParams({
          basePath:
            TsriEndpointResolver.getInstance().peek(
              TIVO_SERVICES.AuthConfigService
            ) || ""
        })
      );
      return service.getAuthConfigurationByType(authenticationType);
    } catch (error: unknown) {
      Log.e(SignInInteractor.TAG, "getAuthConfigurationByType()", error);
      throw error;
    }
  }

  public async fetchClaim(
    params: TDomainTokensGetClaimParams,
    signal?: AbortSignal
  ): Promise<Success> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "fetchClaim() called");
      const service: DomainTokensService = new DomainTokensService(
        await SignInInteractor.buildDomainTokensServicesParams()
      );
      const response: Success = await service.fetchClaim(params, signal);
      this.validateClaimResponse(response);
      SignInLogger.logApiSuccess(SignInApi.Claim, Date.now() - startTime);
      return response;
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.Claim,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTime
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(SignInInteractor.TAG, "fetchClaim() validation failed", error);
        throw error;
      }
      const claimError: ClaimServiceError =
        SignInInteractor.buildClaimServiceError(error);
      Log.e(SignInInteractor.TAG, "fetchClaim() failed", claimError);
      throw claimError;
    }
  }

  public async fetchDomainToken(
    params: TDomainTokensGetDomainTokenParams,
    signal?: AbortSignal
  ): Promise<DomainToken> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "fetchDomainToken() called");
      const service: DomainTokensService = new DomainTokensService(
        await SignInInteractor.buildDomainTokensServicesParams()
      );
      const response: DomainToken = await service.fetchDomainToken(
        params,
        signal
      );
      this.validateDomainTokenResponse(response);
      SignInLogger.logApiSuccess(SignInApi.DomainToken, Date.now() - startTime);
      return response;
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.DomainToken,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTime
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "fetchDomainToken() validation failed",
          error
        );
        throw error;
      }
      const domainTokenError: DomainTokenServiceError | undefined =
        SignInInteractor.buildDomainTokenServiceError(error);
      if (domainTokenError !== undefined) {
        Log.e(
          SignInInteractor.TAG,
          "fetchDomainToken() failed",
          domainTokenError
        );
        throw domainTokenError;
      }
      Log.e(SignInInteractor.TAG, "fetchDomainToken() failed", error);
      throw error;
    }
  }

  public async fetchDeviceDomainToken(
    params: TDomainTokensGetDeviceDomainTokenParams,
    signal?: AbortSignal
  ): Promise<DomainToken> {
    try {
      Log.d(SignInInteractor.TAG, "fetchDeviceDomainToken() called");
      const service: DomainTokensService = new DomainTokensService(
        await SignInInteractor.buildDomainTokensServicesParams()
      );
      return await service.fetchDeviceDomainToken(params, signal);
    } catch (error: unknown) {
      Log.e(SignInInteractor.TAG, "fetchDeviceDomainToken()", error);
      throw error;
    }
  }

  public async fetchScopedDomainToken(
    params: TDomainTokensGetScopedDomainTokenParams,
    signal?: AbortSignal
  ): Promise<DomainToken> {
    try {
      Log.d(SignInInteractor.TAG, "fetchScopedDomainToken() called");
      const service: DomainTokensService = new DomainTokensService(
        await SignInInteractor.buildDomainTokensServicesParams()
      );
      return await service.fetchScopedDomainToken(params, signal);
    } catch (error: unknown) {
      Log.e(SignInInteractor.TAG, "fetchScopedDomainToken()", error);
      throw error;
    }
  }

  public async fetchBind(
    params: TDomainTokensGetBindParams,
    signal?: AbortSignal
  ): Promise<Bind> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "fetchBind() called");
      const service: DomainTokensService = new DomainTokensService(
        await SignInInteractor.buildDomainTokensServicesParams()
      );
      const response: Bind = await service.fetchBind(params, signal);
      SignInLogger.logApiSuccess(SignInApi.Bind, Date.now() - startTime);
      return response;
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.Bind,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTime
      );
      const bindError: BindServiceError =
        SignInInteractor.buildBindServiceError(error);
      Log.e(SignInInteractor.TAG, "fetchBind() failed", bindError);
      throw bindError;
    }
  }

  public async createLicensePlateV2(
    params: LicensePlateRequestV2,
    dbsToken: string,
    signal?: AbortSignal
  ): Promise<LicensePlateResponseV2> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "createLicensePlateV2() called");
      const service: DeviceBindingService = new DeviceBindingService(
        await SignInInteractor.buildDeviceBindingServicesParams(dbsToken)
      );
      const response: LicensePlateResponseV2 =
        await service.createLicensePlateV2(params, signal);
      SignInLogger.logApiSuccess(
        SignInApi.LicensePlateV2,
        Date.now() - startTime
      );
      return response;
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.LicensePlateV2,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTime
      );
      const deviceBindingError: DeleteDeviceBindingServiceError =
        SignInInteractor.buildDeviceBindingServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "createLicensePlateV2() failed",
        deviceBindingError
      );
      throw deviceBindingError;
    }
  }

  public async createDbsJwtToken(
    params: DbsJwtTokenRequest,
    dbsToken: string,
    signal?: AbortSignal
  ): Promise<DbsJwtTokenResponse> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "createDbsJwtToken() called");
      const service: DeviceBindingService = new DeviceBindingService(
        await SignInInteractor.buildDeviceBindingServicesParams(dbsToken)
      );
      const response: DbsJwtTokenResponse = await service.createDbsJwtToken(
        params,
        signal
      );
      SignInLogger.logApiSuccess(SignInApi.DbsJwtToken, Date.now() - startTime);
      return response;
    } catch (error: unknown) {
      // Deliberately not logged here. This endpoint is the DBS binding poll:
      // it is called every few seconds for the life of the license plate and
      // fails on every tick until the user completes the bind on their phone.
      // Logging each failure would evict earlier entries from the bounded
      // pending-log queue. Poll outcomes are logged — sampled — by
      // SignInLogger.logDbsPollAttempt() in dbsLicensePlate.thunks.ts.
      const deviceBindingError: DeleteDeviceBindingServiceError =
        SignInInteractor.buildDeviceBindingServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "createDbsJwtToken() failed",
        deviceBindingError
      );
      throw deviceBindingError;
    }
  }

  /**
   * Builds the TServicesParams used to instantiate TivoIdentityAuthServiceService.
   * The basePath is resolved from the TSRI endpoints catalog rather than
   * from the auth-config `authenticationUrl`.
   */
  private static async buildTivoIdentityAuthServicesParams(): Promise<TServicesParams> {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath:
        await new AppStartupInteractor().getServiceEndpointByServiceName(
          TIVO_SERVICES.TivoIdentityAuthService
        )
    });
  }

  /**
   * Builds the TServicesParams for a TivoIdentityAuthServiceService call whose
   * host comes from the auth-config service rather than the TSRI endpoints
   * catalog — the IP auto-login flow, which is advertised per MSO as
   * `idpMetaData.authenticationUrl`.
   *
   * The advertised URL is used verbatim, operation path included — the
   * repository POSTs to this basePath directly rather than going through the
   * generated client, which would append `/v1/ip-autologin/token` a second
   * time. `new URL()` is kept so a malformed URL rejects here rather than
   * producing a bad request.
   */
  private static buildTivoIdentityAuthServicesParamsFromUrl(
    authenticationUrl: string
  ): TServicesParams {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: new URL(authenticationUrl).href
    });
  }

  public async fetchIdpJwtToken(
    email: string,
    password: string,
    deviceId: string,
    signal?: AbortSignal
  ): Promise<string> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "fetchIdpJwtToken() called");

      const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
      const service: TivoIdentityAuthServiceService =
        new TivoIdentityAuthServiceService(
          await SignInInteractor.buildTivoIdentityAuthServicesParams()
        );

      const tokenResponse = await service.fetchIdpJwtToken(
        {
          tokenRequest: {
            grantType: "password",
            username: email,
            password: password,
            msoName: msoName
          }
        },
        signal
      );

      const response = tokenResponse as { jwt?: string };
      if (
        response.jwt === undefined ||
        response.jwt === null ||
        response.jwt.length === 0
      ) {
        // The service reported success, so there is no "IAS.*" code to map;
        // the sentinel is what earns this its own A411 rather than the
        // "unknown error" default.
        throw new IdpJwtTokenServiceError(
          IDP_JWT_TOKEN_EMPTY_TOKEN_CODE,
          "IDP JWT token is empty in service response"
        );
      }

      SignInLogger.logApiSuccess(SignInApi.IdpJwtToken, Date.now() - startTime);
      return response.jwt;
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.IdpJwtToken,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTime
      );
      // Already carries its A4xx code — re-wrapping would re-run setCode on a
      // catalog number the switch does not know and downgrade it to A412.
      if (error instanceof IdpJwtTokenServiceError) {
        Log.e(SignInInteractor.TAG, "fetchIdpJwtToken()", error);
        throw error;
      }
      const idpJwtTokenError: IdpJwtTokenServiceError =
        SignInInteractor.buildIdpJwtTokenServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "fetchIdpJwtToken() failed",
        idpJwtTokenError
      );
      throw idpJwtTokenError;
    }
  }

  public async forgotPassword(
    email: string,
    signal?: AbortSignal
  ): Promise<void> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "forgotPassword() called");

      const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
      const service: TivoIdentityAuthServiceService =
        new TivoIdentityAuthServiceService(
          await SignInInteractor.buildTivoIdentityAuthServicesParams()
        );

      const response: UpdatePasswordSuccessResponse =
        await service.forgotPassword(
          {
            forgotPasswordRequest: {
              email,
              msoName
            }
          },
          signal
        );
      SignInLogger.logApiSuccess(
        SignInApi.ForgotPassword,
        Date.now() - startTime
      );
      this.validateForgotPasswordResponse(response);
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.ForgotPassword,
        error,
        DiagnosticSeverity.Warning,
        Date.now() - startTime
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "forgotPassword() validation failed",
          error
        );
        throw error;
      }
      const forgotPasswordError: ForgotPasswordServiceError =
        SignInInteractor.buildForgotPasswordServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "forgotPassword() failed",
        forgotPasswordError
      );
      throw forgotPasswordError;
    }
  }

  public async validateEmailToken(
    emailToken: string,
    signal?: AbortSignal
  ): Promise<ValidateEmailTokenResult> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "validateEmailToken() called");

      const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
      const service: TivoIdentityAuthServiceService =
        new TivoIdentityAuthServiceService(
          await SignInInteractor.buildTivoIdentityAuthServicesParams()
        );

      const response: ValidateEmailTokenResponse =
        await service.validateEmailToken(
          {
            validateEmailTokenRequest: {
              emailToken,
              msoName
            }
          },
          signal
        );

      this.validateValidateEmailTokenResponse(response);

      const partnerCustomerId: string =
        response.partnerCustomerId !== undefined &&
        response.partnerCustomerId !== null
          ? response.partnerCustomerId
          : "";
      SignInLogger.logApiSuccess(
        SignInApi.ValidateEmailToken,
        Date.now() - startTime
      );

      return {
        partnerCustomerId,
        identityId: response.identityId,
        msoName: response.msoName,
        email: response.email
      };
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.ValidateEmailToken,
        error,
        DiagnosticSeverity.Warning,
        Date.now() - startTime
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "validateEmailToken() validation failed",
          error
        );
        throw error;
      }
      const validateEmailTokenError: ValidateEmailTokenServiceError =
        SignInInteractor.buildValidateEmailTokenServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "validateEmailToken() failed",
        validateEmailTokenError
      );
      throw validateEmailTokenError;
    }
  }

  public async updatePassword(
    payload: ValidateEmailTokenResult,
    password: string,
    signal?: AbortSignal
  ): Promise<void> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "updatePassword() called");

      const service: TivoIdentityAuthServiceService =
        new TivoIdentityAuthServiceService(
          await SignInInteractor.buildTivoIdentityAuthServicesParams()
        );

      const response: UpdatePasswordSuccessResponse =
        await service.updatePassword(
          {
            updatePasswordRequest: {
              partnerCustomerId: payload.partnerCustomerId,
              identityId: payload.identityId,
              msoName: payload.msoName,
              password
            }
          },
          signal
        );
      SignInLogger.logApiSuccess(
        SignInApi.UpdatePassword,
        Date.now() - startTime
      );
      this.validateUpdatePasswordResponse(response);
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.UpdatePassword,
        error,
        DiagnosticSeverity.Warning,
        Date.now() - startTime
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "updatePassword() validation failed",
          error
        );
        throw error;
      }
      const updatePasswordError: UpdatePasswordServiceError =
        SignInInteractor.buildUpdatePasswordServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "updatePassword() failed",
        updatePasswordError
      );
      throw updatePasswordError;
    }
  }

  public async ipAutologinToken(
    authenticationUrl: string,
    msoName: string,
    signal?: AbortSignal
  ): Promise<IpAutologinTokenResponse> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "ipAutologinToken() called");

      const service: TivoIdentityAuthServiceService =
        new TivoIdentityAuthServiceService(
          SignInInteractor.buildTivoIdentityAuthServicesParamsFromUrl(
            authenticationUrl
          )
        );

      let overrideIp: string | null = null;
      try {
        overrideIp = localStorage.getItem(STORAGE_KEY_OVERRIDE_IP_AUTOLOGIN);
      } catch (error: unknown) {
        Log.w(
          SignInInteractor.TAG,
          "ipAutologinToken() Unable to access localStorage for IP override",
          error
        );
      }

      const tokenResponse: IpAutologinTokenResponse =
        await service.ipAutologinToken(
          {
            ipAutologinTokenRequest: {
              msoName
            },
            xForwardedForTESTINGONLY:
              overrideIp !== null && overrideIp.length > 0
                ? overrideIp
                : undefined
          },
          signal
        );

      this.validateIpAutologinTokenResponse(tokenResponse);

      SignInLogger.logApiSuccess(
        SignInApi.IpAutologinToken,
        Date.now() - startTime
      );
      return tokenResponse;
    } catch (error: unknown) {
      // Warning, not Error: every ipAutologinToken failure is fallback-eligible
      // — the orchestrator moves on to the next auth type, so this is not on
      // its own a terminal sign-in failure.
      SignInLogger.logApiFailure(
        SignInApi.IpAutologinToken,
        error,
        DiagnosticSeverity.Warning,
        Date.now() - startTime
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          SignInInteractor.TAG,
          "ipAutologinToken() validation failed",
          error
        );
        throw error;
      }
      const ipAutologinError: IpAutologinServiceError | undefined =
        SignInInteractor.buildIpAutologinServiceError(error);
      if (ipAutologinError !== undefined) {
        Log.e(
          SignInInteractor.TAG,
          "ipAutologinToken() failed",
          ipAutologinError
        );
        throw ipAutologinError;
      }
      Log.e(SignInInteractor.TAG, "ipAutologinToken() failed", error);
      throw error;
    }
  }

  public async createProvisionDevices(
    params: TDeviceProvisionServiceProvisionDevicesParams,
    domainToken: string,
    signal?: AbortSignal
  ): Promise<ProvisionDevicesResponse> {
    try {
      Log.d(SignInInteractor.TAG, "createProvisionDevices() called");
      const service: DeviceProvisionServiceService =
        new DeviceProvisionServiceService(
          await SignInInteractor.buildDeviceProvisionServicesParams(domainToken)
        );
      return await service.createProvisionDevices(params, signal);
    } catch (error: unknown) {
      const deviceProvisionError: DeviceProvisionServiceError =
        SignInInteractor.buildDeviceProvisionServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "createProvisionDevices() failed",
        deviceProvisionError
      );
      throw deviceProvisionError;
    }
  }

  public async createProvisionDevicesV2(
    params: TDeviceProvisionServiceProvisionDevicesV2Params,
    domainToken: string,
    signal?: AbortSignal
  ): Promise<ProvisionDevicesResponseV2> {
    const startTime: number = Date.now();
    try {
      Log.d(SignInInteractor.TAG, "createProvisionDevicesV2() called");
      const service: DeviceProvisionServiceService =
        new DeviceProvisionServiceService(
          await SignInInteractor.buildDeviceProvisionServicesParams(domainToken)
        );
      const response: ProvisionDevicesResponseV2 =
        await service.createProvisionDevicesV2(params, signal);
      SignInLogger.logApiSuccess(
        SignInApi.ProvisionDevicesV2,
        Date.now() - startTime
      );
      return response;
    } catch (error: unknown) {
      SignInLogger.logApiFailure(
        SignInApi.ProvisionDevicesV2,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTime
      );
      const deviceProvisionError: DeviceProvisionServiceError =
        SignInInteractor.buildDeviceProvisionServiceError(error);
      Log.e(
        SignInInteractor.TAG,
        "createProvisionDevicesV2() failed",
        deviceProvisionError
      );
      throw deviceProvisionError;
    }
  }
}
